import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  useTheme,
  Divider,
  TextField,
  FormControlLabel,
  Switch,
  Paper,
  Avatar,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  InputAdornment,
  LinearProgress,
  Snackbar,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningIcon from '@mui/icons-material/Warning';
import CheckInIcon from '@mui/icons-material/Login';
import CheckOutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HotelIcon from '@mui/icons-material/Hotel';
import ToggleButton from '@mui/material/ToggleButton';
import PaymentIcon from '@mui/icons-material/Payment';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RoomIcon from '@mui/icons-material/Room';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { getCheckOutRooms, processCheckOut, updateBookingCheckoutDate, getBookingGuestDocuments } from '../../services/api';
import api from '../../services/api';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import { formatPaymentTime, toPaymentProcessedAtIso } from '../../utils/dateUtils';
import { resolveStayCheckInDisplay, resolveStayCheckOutDisplay, resolveActualCheckInDisplay, resolveActualCheckOutDisplay, isSameDayStay } from '../../utils/bookingStayDisplay';
import {
  buildRoomTypeSortOrderMap,
  resolveRoomTypeChangeKindFromItems,
  getRoomTypeChangeLabel,
} from '../../utils/bookingUtils';
import ActualStayDateChips from './ActualStayDateChips';
import StayDurationChip from './StayDurationChip';
import moment from 'moment';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { HotelDateTimePicker as DateTimePicker } from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import ChannelImage from '../common/ChannelImage';
import PaymentLinkDrawerOverlay from './PaymentLinkDrawerOverlay';
import PaymentDrawerHeaderTitle from './PaymentDrawerHeaderTitle';
import {
  filterPaymentMethodsForComm,
  PAYMENT_LINK_METHOD,
  useBookingPaymentLinkComm,
} from '../../hooks/useBookingPaymentLinkComm';
import { PAYMENT_METHOD_CHIPS } from '../../utils/paymentMethodUtils';
import {
  getBookingDrawerTheme,
  getBookingDrawerPaperSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerFooterSx,
  getBookingDrawerPanelSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  getBookingDrawerAuditButtonSx,
  getBookingDrawerInputBg,
  getBookingDrawerDividerColor,
  bookingDrawerRoomImageSizeSx,
  createBookingDrawerOverlayProps,
  BOOKING_DRAWER_ACCENT,
} from '../../utils/bookingDrawerLayout';
import { getDrawerRootSx } from '../../utils/drawerOverlayProps';
import { GuestDocumentsDrawer, buildGuestDocumentsPayload } from './guestDocuments';
import BookingAuditDrawer from './BookingAuditDrawer';
import {
  getBookingCurrencySymbol,
  getPaymentSummaryData,
  getEnrichedPaymentSummaryData,
} from '../../utils/bookingPaymentSummary';
import BookingPaymentSummaryCard from './BookingPaymentSummaryCard';
import BookingDrawerPaymentCard from './BookingDrawerPaymentCard';
import BookingFinancialHistoryPanel from './BookingFinancialHistoryPanel';
import { BookingDrawerHeaderIdentity, DrawerGuestFieldLink } from './DrawerEntityLink';
import { getPermissionDeniedMessage, PERMISSIONS } from '../../auth/permissions';
import usePermission from '../../auth/usePermission';

const CUSTOM_BORDER_COLOR = '#4ce1b6';

const CheckOutDrawer = ({ bookingId, open, onClose, onCheckOutSuccess, onModifyBooking, onBookingUpdated }) => {
  const { can } = usePermission();
  const canViewBookingAudit = can(PERMISSIONS.BOOKING_AUDIT_VIEW);
  const canEditBooking = can(PERMISSIONS.BOOKING_MODIFY);
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const drawerTheme = getBookingDrawerTheme(isDark);
  const { datePickerSlotProps } = useMemo(
    () => createBookingDrawerOverlayProps(isDark),
    [isDark],
  );

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [auditDrawerOpen, setAuditDrawerOpen] = useState(false);
  const [allocatedRooms, setAllocatedRooms] = useState(null);
  const [booking, setBooking] = useState(null);
  const [bookingItems, setBookingItems] = useState([]);
  const [selectedRooms, setSelectedRooms] = useState([]); // Array of room IDs to checkout
  const [guestSlideIndex, setGuestSlideIndex] = useState({}); // {roomId: slideIndex} for guest slider per room
  const [documentDrawerOpen, setDocumentDrawerOpen] = useState(false);
  const [selectedGuestDocuments, setSelectedGuestDocuments] = useState(null); // {id_front_url, id_back_url, document_type, guestName, guestPhone, bookingCode}
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentDate, setPaymentDate] = useState(() => moment());
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentLinkSendOpen, setPaymentLinkSendOpen] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [paymentDrawerOpen, setPaymentDrawerOpen] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [paymentCameraOpen, setPaymentCameraOpen] = useState(false);
  const [paymentStream, setPaymentStream] = useState(null);
  const [paymentCameraUpload, setPaymentCameraUpload] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [cameraPermissionHint, setCameraPermissionHint] = useState({ open: false, message: '' });
  const [dueConfirmOpen, setDueConfirmOpen] = useState(false);
  const [dueConfirmInput, setDueConfirmInput] = useState('');
  const [dueConfirmError, setDueConfirmError] = useState('');
  const [dueConfirmAmount, setDueConfirmAmount] = useState(0);
  const paymentReceiptVideoRef = useRef(null);
  const paymentReceiptCanvasRef = useRef(null);
  const paymentReceiptFileInputRef = useRef(null);
  const paymentDrawerRootRef = useRef(null);
  
  // Payment methods matching check-in drawer
  const {
    canSendPaymentLink,
    canSendPaymentLinkEmail,
    canSendPaymentLinkWhatsApp,
  } = useBookingPaymentLinkComm();
  const paymentMethods = useMemo(
    () => filterPaymentMethodsForComm(PAYMENT_METHOD_CHIPS, canSendPaymentLink),
    [canSendPaymentLink]
  );
  useEffect(() => {
    if (!canSendPaymentLink && paymentMethod === PAYMENT_LINK_METHOD) {
      setPaymentMethod('');
      setPaymentLinkSendOpen(false);
    }
  }, [canSendPaymentLink, paymentMethod]);
  const [extendCheckoutDate, setExtendCheckoutDate] = useState(null);
  const [extendStay, setExtendStay] = useState(false);
  const [savingExtendStay, setSavingExtendStay] = useState(false);
  const [bookingInfoExpanded, setBookingInfoExpanded] = useState(true);
  const auditBookingId = useMemo(() => {
    const parsedId = Number(booking?.id || booking?.booking_id || bookingId);
    return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
  }, [booking?.id, booking?.booking_id, bookingId]);
  const stayCheckInDisplay = useMemo(() => resolveStayCheckInDisplay(booking), [booking]);
  const stayCheckOutDisplay = useMemo(() => resolveStayCheckOutDisplay(booking), [booking]);
  const actualCheckInDisplay = useMemo(() => resolveActualCheckInDisplay(booking), [booking]);
  const actualCheckOutDisplay = useMemo(() => resolveActualCheckOutDisplay(booking), [booking]);

  // Detect sequences where allocated room changed across dates (same room_type + room_sequence)
  const changedSequenceKeys = useMemo(() => {
    const sequenceToRooms = new Map();
    (bookingItems || []).forEach((item) => {
      const roomTypeId = item.room_type_id ?? item.room_type?.id;
      if (!roomTypeId) return;
      const seq = item.room_sequence || 1;
      const key = `${roomTypeId}_${seq}`;
      const roomId = item.allocated_room_id || null;
      if (!roomId) return;
      if (!sequenceToRooms.has(key)) sequenceToRooms.set(key, new Set());
      sequenceToRooms.get(key).add(Number(roomId));
    });
    const changed = new Set();
    sequenceToRooms.forEach((roomIds, key) => {
      if (roomIds.size > 1) changed.add(key);
    });
    return changed;
  }, [bookingItems]);

  // Detect sequences where room type changes across dates (upgrade flow)
  const upgradedRoomTypeKeys = useMemo(() => {
    const sequenceRoomTypeStartDate = new Map();
    const sequenceItems = new Map();
    const explicitUpgradeCandidates = new Set();
    const getItemDateKey = (item) => {
      if (!item?.date) return '';
      return moment.isMoment(item.date) ? item.date.format('YYYY-MM-DD') : String(item.date).slice(0, 10);
    };
    (bookingItems || []).forEach((item) => {
      const roomTypeId = Number((item.room_type_id ?? item.room_type?.id) || 0);
      if (!roomTypeId) return;
      const seq = Number(item.room_sequence || 1);
      if (!sequenceItems.has(seq)) sequenceItems.set(seq, []);
      sequenceItems.get(seq).push(item);
      const upgradeFromTypeId = Number(
        (item.upgraded_from_room_type_id ?? item.upgraded_from_room_type?.id) || 0
      );
      if (upgradeFromTypeId && upgradeFromTypeId !== roomTypeId) {
        explicitUpgradeCandidates.add(`${seq}_${roomTypeId}`);
      }
      const itemDate = getItemDateKey(item);
      if (!itemDate) return;
      if (!sequenceRoomTypeStartDate.has(seq)) sequenceRoomTypeStartDate.set(seq, new Map());
      const perType = sequenceRoomTypeStartDate.get(seq);
      const currentStart = perType.get(roomTypeId);
      if (!currentStart || itemDate < currentStart) {
        perType.set(roomTypeId, itemDate);
      }
    });

    const upgraded = new Set();
    sequenceRoomTypeStartDate.forEach((perType, seq) => {
      const dates = [...perType.values()].filter(Boolean);
      if (dates.length <= 1) return;
      const sequenceStartDate = dates.reduce((minDate, d) => (d < minDate ? d : minDate), dates[0]);
      const seqItems = sequenceItems.get(seq) || [];
      perType.forEach((typeStartDate, rtId) => {
        // Mark fallback upgrade only for real mid-stay upgrades with prior checked-out history.
        const hasPriorCheckedOutHistory = seqItems.some((it) => {
          const itTypeId = Number((it.room_type_id ?? it.room_type?.id) || 0);
          if (!itTypeId || itTypeId === rtId) return false;
          const itDate = getItemDateKey(it);
          if (!itDate || itDate >= typeStartDate) return false;
          return it.checkout === true;
        });
        const key = `${seq}_${rtId}`;
        const isExplicitCandidate = explicitUpgradeCandidates.has(key);
        if ((isExplicitCandidate || typeStartDate > sequenceStartDate) && hasPriorCheckedOutHistory) {
          upgraded.add(key);
        }
      });
    });

    return upgraded;
  }, [bookingItems]);

  const roomTypeSortOrderById = useMemo(
    () => buildRoomTypeSortOrderMap(bookingItems),
    [bookingItems],
  );

  // Fetch allocated rooms when drawer opens
  useEffect(() => {
    if (open && bookingId) {
      fetchAllocatedRooms();
    } else {
      // Reset state when drawer closes
      setAllocatedRooms(null);
      setBooking(null);
      setSelectedRooms([]);
      setPaymentAmount(0);
      setPaymentDate(moment());
      setPaymentMethod('');
      setPaymentLinkSendOpen(false);
      setReferenceNumber('');
      setError(null);
      setExtendCheckoutDate(null);
      setExtendStay(false);
      setPaymentDrawerOpen(false);
      setPaymentHistory([]);
      setPaymentReceipt(null);
      setPaymentCameraUpload(false);
      setAuditDrawerOpen(false);
      setSuccessMessage(null);
      setBookingItems([]);
      setGuestSlideIndex({});
      setDocumentDrawerOpen(false);
      setSelectedGuestDocuments(null);
      setDueConfirmOpen(false);
      setDueConfirmInput('');
      setDueConfirmError('');
      setDueConfirmAmount(0);
      // Stop camera if open
      if (paymentStream) {
        paymentStream.getTracks().forEach(track => track.stop());
        setPaymentStream(null);
      }
      setPaymentCameraOpen(false);
    }
  }, [open, bookingId, paymentStream]);

  // Update payment amount when booking data changes
  useEffect(() => {
    if (booking) {
      // Use payment_summary if available, otherwise calculate from booking fields
      const paymentSummary = booking.payment_summary;
      const remainingAmount = paymentSummary?.remaining_balance !== undefined
        ? paymentSummary.remaining_balance
        : (booking.remaining_amount !== undefined && booking.remaining_amount !== null
        ? booking.remaining_amount
          : ((booking.total_amount || booking.total || 0) - (booking.advance_amount || booking.advance || booking.advance_paid || 0)));
      
      const round2 = (x) => Math.round(Number(x) * 100) / 100;
      if (remainingAmount !== undefined && remainingAmount !== null) {
        setPaymentAmount(round2(remainingAmount));
      }
    }
  }, [booking?.payment_summary, booking?.remaining_amount, booking?.total_amount, booking?.advance_amount, booking]);

  // Lazy load payment history only when payment drawer opens
  useEffect(() => {
    if (paymentDrawerOpen && booking?.code) {
      fetchPaymentHistory(booking.code || booking.booking_code);
    }
  }, [paymentDrawerOpen, booking?.code, booking?.booking_code]);

  const fetchPaymentHistory = async (bookingCode) => {
    if (!bookingCode) return;
    setLoadingPayments(true);
    try {
      const response = await api.get('/finance/api/payments/', {
        params: { booking_code: bookingCode }
      });
      const data = response.data;
      let payments = [];
      if (data) {
        if (Array.isArray(data)) {
          payments = data;
        } else if (data.Data && Array.isArray(data.Data)) {
          payments = data.Data;
        } else if (data.data && Array.isArray(data.data)) {
          payments = data.data;
        } else if (data.results && Array.isArray(data.results)) {
          payments = data.results;
        } else if (data.payments && Array.isArray(data.payments)) {
          payments = data.payments;
        }
      }
      setPaymentHistory(payments);
    } catch (err) {setPaymentHistory([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  const startPaymentCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraPermissionHint({
        open: true,
        message: 'Camera is not available. Turn off Camera upload to upload a receipt image from files.',
      });
      return;
    }
    try {
      if (paymentStream) {
        paymentStream.getTracks().forEach(track => track.stop());
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      setPaymentStream(mediaStream);
      setPaymentCameraOpen(true);
      setTimeout(() => {
        if (paymentReceiptVideoRef.current) {
          paymentReceiptVideoRef.current.srcObject = mediaStream;
          paymentReceiptVideoRef.current.play().catch(() => {});
        }
      }, 100);
    } catch (error) {
      setCameraPermissionHint({
        open: true,
        message:
          'Camera access was denied or is unavailable. Allow camera permission in your browser settings, or turn off Camera upload to choose a file.',
      });
    }
  };

  const stopPaymentCamera = () => {
    if (paymentStream) {
      paymentStream.getTracks().forEach(track => track.stop());
      setPaymentStream(null);
    }
    setPaymentCameraOpen(false);
  };

  const capturePaymentReceipt = () => {
    if (paymentReceiptVideoRef.current && paymentReceiptCanvasRef.current) {
      const video = paymentReceiptVideoRef.current;
      const canvas = paymentReceiptCanvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setPaymentReceipt(imageData);
      stopPaymentCamera();
    }
  };

  const handlePaymentReceiptUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setPaymentReceipt(event.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        setError('Please upload an image file.');
      }
    }
  };

  const handlePaymentReceiptClick = () => {
    if (paymentCameraUpload) {
      startPaymentCamera();
    } else {
      paymentReceiptFileInputRef.current?.click();
    }
  };

  const handleSubmitPayment = async () => {
    if (!paymentAmount || paymentAmount <= 0) {
      setPaymentError('Please enter a valid payment amount.');
      return;
    }
    if (!paymentMethod) {
      setPaymentError('Please select a payment method.');
      return;
    }
    if (paymentMethod === 'PAYMENT LINK') {
      setPaymentError('Use Email, WhatsApp, or Both in the payment link panel.');
      return;
    }

    setSubmittingPayment(true);
    setPaymentError(null);
    try {
      const processedAtIso = toPaymentProcessedAtIso(paymentDate);
      let response;
      if (paymentReceipt) {
        const formData = new FormData();
        formData.append('booking', bookingId);
        formData.append('amount', paymentAmount);
        formData.append('payment_type', 'ADVANCE');
        formData.append('payment_method', paymentMethod);
        if (referenceNumber) {
          formData.append('reference_number', referenceNumber);
        }
        formData.append('status', 'SUCCESS');
        if (processedAtIso) formData.append('processed_at', processedAtIso);
        
        if (paymentReceipt.startsWith('data:image')) {
          const blobResponse = await fetch(paymentReceipt);
          const blob = await blobResponse.blob();
          formData.append('document', blob, 'payment_receipt.jpg');
        } else if (paymentReceipt instanceof File) {
          formData.append('document', paymentReceipt);
        }

        response = await api.post('/finance/api/payments/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        response = await api.post('/finance/api/payments/', {
          booking: bookingId,
          amount: paymentAmount,
          payment_type: 'ADVANCE',
          payment_method: paymentMethod,
          reference_number: referenceNumber || null,
          status: 'SUCCESS',
          processed_at: processedAtIso,
        });
      }

      if (response.data) {
      setPaymentAmount(0);
        setPaymentDate(moment());
        setPaymentMethod('');
        setReferenceNumber('');
        setPaymentReceipt(null);
        setPaymentCameraUpload(false);
        setPaymentError(null);
        setPaymentDrawerOpen(false);
        setSuccessMessage('Payment collected successfully.');
        setSubmittingPayment(false);
        // Patch booking card immediately; refresh drawer data in background.
        onBookingUpdated?.(bookingId);
        void (async () => {
          try {
            await Promise.all([
              fetchPaymentHistory(booking?.code || booking?.booking_code),
              fetchAllocatedRooms(false),
            ]);
          } catch {
            // Keep drawer usable; card already patched.
          }
        })();
      }
    } catch (err) {
      setPaymentError(err.response?.data?.error || err.message || 'Failed to process payment.');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const fetchAllocatedRooms = async (showLoading = true) => {
    if (showLoading) {
    setLoading(true);
    }
    setError(null);
    try {
      const data = await getCheckOutRooms(bookingId);
      setAllocatedRooms(data.room_types || []);
      setBooking(data.booking);
      setBookingItems(data.booking_items || []);
    } catch (err) {setError('Failed to load allocated rooms. Please try again.');
    } finally {
      if (showLoading) {
      setLoading(false);
      }
    }
  };

  const handleRoomToggle = (roomId) => {
    // Don't allow room selection if booking is checked out
    if (String(booking?.status || '').toLowerCase() === 'checked_out') {
      return;
    }

    // Only allow selection when this room has at least one non-checked-out booking item.
    const itemsForRoom = (bookingItems || []).filter(
      (item) => item?.allocated_room_id === roomId
    );
    const allItemsCheckedOut =
      itemsForRoom.length > 0 && itemsForRoom.every((item) => item.checkout === true);
    if (allItemsCheckedOut) {
      return;
    }

    setSelectedRooms((prev) => {
      if (prev.includes(roomId)) {
        return prev.filter((id) => id !== roomId);
      } else {
        return [...prev, roomId];
      }
    });
  };

  const handleSaveExtendStay = async () => {
    if (!extendCheckoutDate || !bookingId) {
      setError('Please select a new checkout date.');
      return;
    }

    setSavingExtendStay(true);
    setError(null);

    try {
      // Update booking checkout date directly
      const newCheckoutDate = moment(extendCheckoutDate).format('YYYY-MM-DD');
      const result = await updateBookingCheckoutDate(bookingId, newCheckoutDate);
      
      if (result) {
        // Refresh booking data to get updated information including recalculated remaining amount
        await fetchAllocatedRooms();
        setError(null);
        // Show success feedback (the booking data will be refreshed)
      }
    } catch (err) {const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to extend stay. Please try again.';
      setError(errorMsg);
    } finally {
      setSavingExtendStay(false);
    }
  };

  const getOutstandingDueAmount = () => {
    if (!booking) return 0;
    const enriched = getEnrichedPaymentSummaryData(booking, paymentHistory);
    if (!enriched) return 0;
    const remaining = enriched.hasRestaurant
      ? Number(enriched.combinedRemaining ?? 0)
      : Number(enriched.remainingAmount ?? enriched.hotelRemaining ?? 0);
    if (!Number.isFinite(remaining)) return 0;
    return Math.round(Math.max(0, remaining) * 100) / 100;
  };

  const getOutstandingDueBreakdown = () => {
    if (!booking) return null;
    const enriched = getEnrichedPaymentSummaryData(booking, paymentHistory);
    if (!enriched?.hasRestaurant) return null;
    return {
      hotelDue: Number(enriched.hotelRemaining ?? 0),
      restaurantDue: Number(enriched.restaurantRemaining ?? 0),
      combinedDue: Number(enriched.combinedRemaining ?? 0),
      currency: enriched.currency || booking.currency || 'INR',
    };
  };

  const submitCheckout = async () => {
    if (!bookingId) return;

    setSubmitting(true);
    setError(null);

    try {
      // Prepare checkout data - payment is collected separately through payment drawer
      const checkoutData = {
        ...(extendStay && extendCheckoutDate ? { extend_checkout_date: moment(extendCheckoutDate).format('YYYY-MM-DD') } : {}),
      };

      const result = await processCheckOut(bookingId, selectedRooms, checkoutData);
      if (result.success) {
        if (onCheckOutSuccess) {
          onCheckOutSuccess(result.booking);
        }
        onClose();
      }
    } catch (err) {const errorMsg = err.response?.data?.error || err.message || 'Failed to process checkout. Please try again.';
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckOut = async () => {
    if (!bookingId) return;

    const outstandingDueAmount = getOutstandingDueAmount();
    if (outstandingDueAmount > 0) {
      setDueConfirmAmount(outstandingDueAmount);
      setDueConfirmInput('');
      setDueConfirmError('');
      setDueConfirmOpen(true);
      return;
    }

    await submitCheckout();
  };

  const handleConfirmDueCheckout = async () => {
    if (dueConfirmInput.trim().toLowerCase() !== 'confirm') {
      setDueConfirmError('Type "confirm" to proceed with checkout.');
      return;
    }

    setDueConfirmError('');
    setDueConfirmInput('');
    setDueConfirmOpen(false);
    await submitCheckout();
  };

  // Calculate room checkout state from booking items (source of truth)
  const roomCheckoutStats = {};
  (bookingItems || []).forEach((item) => {
    const roomId = item?.allocated_room_id;
    if (!roomId) return;
    if (!roomCheckoutStats[roomId]) {
      roomCheckoutStats[roomId] = { total: 0, checkedOut: 0 };
    }
    roomCheckoutStats[roomId].total += 1;
    if (item.checkout === true) {
      roomCheckoutStats[roomId].checkedOut += 1;
    }
  });

  const totalRooms = Object.keys(roomCheckoutStats).length;
  const checkedOutRoomsCount = Object.values(roomCheckoutStats).filter(
    (stats) => stats.total > 0 && stats.checkedOut >= stats.total
  ).length;
  const pendingCheckoutRoomsCount = Math.max(0, totalRooms - checkedOutRoomsCount);
  const selectedRoomsCount = selectedRooms.length;
  const hasPendingCheckoutItems = pendingCheckoutRoomsCount > 0;

  const isPartialCheckOut = selectedRoomsCount > 0 && selectedRoomsCount < pendingCheckoutRoomsCount;
  const isBookingCheckedOut = String(booking?.status || '').toLowerCase() === 'checked_out';
  const canCheckOut = selectedRoomsCount > 0 && !isBookingCheckedOut && hasPendingCheckoutItems;
  const wantsModifyBooking = isBookingCheckedOut || !hasPendingCheckoutItems;
  const showModifyBooking = wantsModifyBooking && Boolean(onModifyBooking) && canEditBooking;
  const showDisabledModifyBooking = wantsModifyBooking && !showModifyBooking;

  const round2 = (x) => Math.round(Number(x) * 100) / 100;
  const formatCurrency = (amount, currency = 'INR') => {
    if (amount == null || amount === '' || Number(amount) === 0) {
      return `${getBookingCurrencySymbol(currency)}0.00`;
    }
    const value = round2(amount);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };
  const bookingCurrencySymbol = getBookingCurrencySymbol(booking?.currency || 'INR');

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={getDrawerRootSx()}
      SlideProps={{ direction: 'left', appear: true }}
      transitionDuration={{ enter: 350, exit: 250 }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 600 },
          backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1,
            p: 1.5,
            borderBottom: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
          }}
        >
          <BookingDrawerHeaderIdentity
            icon={<CheckOutIcon sx={{ color: CUSTOM_BORDER_COLOR, fontSize: 24, flexShrink: 0 }} />}
            title="Checkout"
            booking={booking}
            bookingId={bookingId}
            onNavigate={onClose}
            isDark={isDark}
            drawerTheme={drawerTheme}
            label={booking?.code}
          />
          {canViewBookingAudit && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<FactCheckIcon sx={{ fontSize: 16 }} />}
            onClick={() => setAuditDrawerOpen(true)}
            disabled={!auditBookingId}
            sx={{
              borderColor: isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.45)',
              color: CUSTOM_BORDER_COLOR,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '999px',
              px: 1.5,
              py: 0.3,
              minWidth: 84,
              flexShrink: 0,
              '&:hover': {
                borderColor: CUSTOM_BORDER_COLOR,
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
              },
              '&.Mui-disabled': {
                borderColor: isDark ? drawerTheme.surfaceBorder : '#d0d0d0',
                color: isDark ? drawerTheme.subtleText : '#999',
              },
            }}
          >
            Audit
          </Button>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }} data-checkout-content>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}


          {!loading && booking && (
            <>
              {/* Booking Information - Same design as CheckInDrawer */}
              <Paper
                elevation={0}
                sx={{
                  mb: 1.5,
                  backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                  border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                  borderRadius: '12px',
                  position: 'relative',
                  p: 1.5,
                }}
              >
                {/* Booking Source Image and Status Chip - Top Right */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {/* Channel Image - Display only if channel is not null */}
                  {(booking?.channel_data || booking?.channel) && (
                    booking?.channel_data ? (
                      <ChannelImage channel={booking.channel_data} size={32} />
                    ) : (
                      <ChannelImage isPMS={true} size={32} />
                    )
                  )}
                  
                  {/* Booking Status Chip */}
                  {booking.status && (
                    <Chip
                      label={booking.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        backgroundColor: (() => {
                          const statusLower = (booking.status || '').toLowerCase();
                          if (statusLower === 'confirmed') {
                            return isDark ? 'rgba(33, 150, 243, 0.15)' : 'rgba(33, 150, 243, 0.1)';
                          } else if (
                            statusLower === 'checked_in' ||
                            statusLower === 'partial_checkin' ||
                            statusLower === 'partial_checkout'
                          ) {
                            return isDark ? 'rgba(76, 175, 80, 0.22)' : 'rgba(76, 175, 80, 0.14)';
                          } else if (statusLower === 'checked_out') {
                            return isDark ? 'rgba(158, 158, 158, 0.15)' : 'rgba(158, 158, 158, 0.1)';
                          } else if (statusLower === 'cancelled' || statusLower === 'no_show') {
                            return isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.1)';
                          } else if (['blocked', 'reserved', 'block'].includes(statusLower)) {
                            return isDark ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.1)';
                          }
                          return isDark ? 'rgba(158, 158, 158, 0.15)' : 'rgba(158, 158, 158, 0.1)';
                        })(),
                        color: (() => {
                          const statusLower = (booking.status || '').toLowerCase();
                          if (statusLower === 'confirmed') {
                            return isDark ? '#64b5f6' : '#1976d2';
                          } else if (
                            statusLower === 'checked_in' ||
                            statusLower === 'partial_checkin' ||
                            statusLower === 'partial_checkout'
                          ) {
                            return isDark ? '#a5d6a7' : '#2e7d32';
                          } else if (statusLower === 'checked_out') {
                            return isDark ? '#bdbdbd' : '#616161';
                          } else if (statusLower === 'cancelled' || statusLower === 'no_show') {
                            return isDark ? '#ef5350' : '#c62828';
                          } else if (['blocked', 'reserved', 'block'].includes(statusLower)) {
                            return isDark ? '#ffb74d' : '#e65100';
                          }
                          return isDark ? '#bdbdbd' : '#616161';
                        })(),
                        border: (() => {
                          const statusLower = (booking.status || '').toLowerCase();
                          if (statusLower === 'confirmed') {
                            return `1px solid ${isDark ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.25)'}`;
                          } else if (
                            statusLower === 'checked_in' ||
                            statusLower === 'partial_checkin' ||
                            statusLower === 'partial_checkout'
                          ) {
                            return `1px solid ${isDark ? 'rgba(76, 175, 80, 0.55)' : 'rgba(76, 175, 80, 0.4)'}`;
                          } else if (statusLower === 'checked_out') {
                            return `1px solid ${isDark ? 'rgba(158, 158, 158, 0.3)' : 'rgba(158, 158, 158, 0.25)'}`;
                          } else if (statusLower === 'cancelled' || statusLower === 'no_show') {
                            return `1px solid ${isDark ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.25)'}`;
                          } else if (['blocked', 'reserved', 'block'].includes(statusLower)) {
                            return `1px solid ${isDark ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.25)'}`;
                          }
                          return `1px solid ${isDark ? 'rgba(158, 158, 158, 0.3)' : 'rgba(158, 158, 158, 0.25)'}`;
                        })(),
                        backdropFilter: 'blur(10px)',
                        boxShadow: isDark 
                          ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
                          : '0 2px 8px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                  )}
                  {isSameDayStay(booking) && (
                    <Chip
                      label="Hourly"
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        backgroundColor: isDark ? 'rgba(255, 167, 38, 0.2)' : 'rgba(255, 167, 38, 0.12)',
                        color: isDark ? '#ffb74d' : '#ef6c00',
                        border: `1px solid ${isDark ? 'rgba(255, 183, 77, 0.45)' : 'rgba(239, 108, 0, 0.35)'}`,
                      }}
                    />
                  )}
                </Box>
                <Grid container spacing={1.5}>
                  {/* Guest Name with Booking Source */}
                  {booking.guest_name && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon sx={{ fontSize: 14, color: CUSTOM_BORDER_COLOR }} />
                          <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                          Guest Name:
                        </Typography>
                          <DrawerGuestFieldLink
                            isDark={isDark}
                            booking={booking}
                            guest={booking.guest}
                            guestId={booking?.guest_id ?? booking?.guest?.id}
                            variant="body2"
                            onNavigate={onClose}
                            sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                          >
                          {booking.guest_name}
                          </DrawerGuestFieldLink>
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {/* Mobile Number */}
                  {(booking.guest_phone || booking.phone) && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <PhoneIcon sx={{ fontSize: 14, color: CUSTOM_BORDER_COLOR }} />
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                          Mobile Number:
                        </Typography>
                        <DrawerGuestFieldLink
                          isDark={isDark}
                          booking={booking}
                          guest={booking.guest}
                          guestId={booking?.guest_id ?? booking?.guest?.id}
                          variant="body2"
                          onNavigate={onClose}
                          sx={{ fontWeight: 500, fontSize: '0.85rem' }}
                        >
                          {booking.guest_phone || booking.phone || '—'}
                        </DrawerGuestFieldLink>
                      </Box>
                    </Grid>
                  )}

                  {/* Check-in Information */}
                  <Grid item xs={12} sm={6}>
                    <Box
                        sx={{
                        p: 1,
                        borderRadius: '8px',
                        background: isDark ? 'rgba(76, 225, 182, 0.08)' : 'rgba(76, 225, 182, 0.05)',
                        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                        minHeight: booking.check_in_time ? 'auto' : '72px',
                          display: 'flex',
                        flexDirection: 'column',
                        }}
                      >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <CheckInIcon sx={{ fontSize: 12, color: CUSTOM_BORDER_COLOR }} />
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem', fontWeight: 600 }}>
                        Check-In
                      </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#212121', fontSize: '0.75rem', mb: 0.25 }}>
                        {stayCheckInDisplay.date || '—'}
                      </Typography>
                      {stayCheckInDisplay.time ? (
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem' }}>
                          {stayCheckInDisplay.time}
                        </Typography>
                      ) : (
                        <Box sx={{ height: '16px' }} />
                      )}
                      {booking.early_checkin && (
                        <Chip
                          label={
                            booking.early_checkin.is_early
                              ? `Early ${booking.early_checkin.hours}h`
                              : `On time`
                          }
                          size="small"
                          sx={{
                            mt: 0.5,
                            backgroundColor: booking.early_checkin.is_early ? '#ff9800' : '#4caf50',
                            color: '#fff',
                            fontSize: '0.65rem',
                            height: 20,
                          }}
                          icon={booking.early_checkin.is_early ? <WarningIcon sx={{ fontSize: 12 }} /> : <CheckCircleIcon sx={{ fontSize: 12 }} />}
                        />
                      )}
                    </Box>
                    <ActualStayDateChips actual={actualCheckInDisplay} label="Actual check-in" isDark={isDark} />
                  </Grid>

                  {/* Check-out Information */}
                  <Grid item xs={12} sm={6}>
                    <Box
                        sx={{
                        p: 1,
                        borderRadius: '8px',
                        background: isDark ? 'rgba(76, 225, 182, 0.08)' : 'rgba(76, 225, 182, 0.05)',
                        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                        minHeight: booking.check_out_time ? 'auto' : '72px',
                          display: 'flex',
                        flexDirection: 'column',
                        }}
                      >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <CheckOutIcon sx={{ fontSize: 12, color: CUSTOM_BORDER_COLOR }} />
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem', fontWeight: 600 }}>
                        Check-Out
                      </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#212121', fontSize: '0.75rem', mb: 0.25 }}>
                        {stayCheckOutDisplay.date || '—'}
                      </Typography>
                      {stayCheckOutDisplay.time ? (
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem' }}>
                          {stayCheckOutDisplay.time}
                        </Typography>
                      ) : (
                        <Box sx={{ height: '16px' }} />
                      )}
                      {booking.early_checkout && (
                        <Chip
                          label={
                            booking.early_checkout.is_early
                              ? `Early ${booking.early_checkout.hours}h`
                              : booking.check_out_time
                                ? `On time`
                                : 'Pending'
                          }
                          size="small"
                          sx={{
                            mt: 0.5,
                            backgroundColor: booking.early_checkout.is_early
                              ? '#ff9800'
                              : booking.check_out_time
                                ? '#4caf50'
                                : '#9e9e9e',
                            color: '#fff',
                            fontSize: '0.65rem',
                            height: 20,
                          }}
                          icon={
                            booking.early_checkout.is_early ? (
                              <WarningIcon sx={{ fontSize: 12 }} />
                            ) : booking.check_out_time ? (
                              <CheckCircleIcon sx={{ fontSize: 12 }} />
                            ) : (
                              <AccessTimeIcon sx={{ fontSize: 12 }} />
                            )
                          }
                        />
                      )}
                    </Box>
                    <ActualStayDateChips actual={actualCheckOutDisplay} label="Actual check-out" isDark={isDark} />
                  </Grid>

                  {/* Extend Stay - Only checkin, partial checkin and confirmed can extend; partial_checkout and checked_out cannot */}
                  {['checked_in', 'partial_checkin', 'confirmed'].includes(booking?.status) && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1, borderColor: isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)' }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: extendStay ? 1 : 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccessTimeIcon sx={{ fontSize: 16, color: CUSTOM_BORDER_COLOR }} />
                          <Typography variant="body2" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 500 }}>
                            Extend Stay
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={extendStay}
                              onChange={(e) => {
                                setExtendStay(e.target.checked);
                                if (!e.target.checked) {
                                  setExtendCheckoutDate(null);
                                } else if (booking && booking.check_out_date) {
                                  setExtendCheckoutDate(moment(booking.check_out_date).add(1, 'day'));
                                }
                              }}
                              size="small"
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: CUSTOM_BORDER_COLOR,
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: CUSTOM_BORDER_COLOR,
                                },
                              }}
                            />
                          }
                          label=""
                          sx={{ m: 0 }}
                        />
                      </Box>
                      {extendStay && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                              label="New Checkout Date"
                              value={extendCheckoutDate}
                              onChange={(newValue) => setExtendCheckoutDate(newValue)}
                              minDate={booking && booking.check_out_date ? moment(booking.check_out_date) : moment()}
                              slotProps={{
                                ...datePickerSlotProps,
                                textField: {
                                  fullWidth: true,
                                  size: 'small',
                                  sx: {
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': {
                                      '& fieldset': {
                                        borderColor: CUSTOM_BORDER_COLOR,
                                      },
                                      '&:hover fieldset': {
                                        borderColor: CUSTOM_BORDER_COLOR,
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: CUSTOM_BORDER_COLOR,
                                      },
                                    },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                          <Button
                            variant="contained"
                            onClick={handleSaveExtendStay}
                            disabled={!extendCheckoutDate || savingExtendStay}
                            size="small"
                            sx={{
                              backgroundColor: CUSTOM_BORDER_COLOR,
                              color: '#000',
                              fontWeight: 600,
                              minWidth: 80,
                              '&:hover': {
                                backgroundColor: CUSTOM_BORDER_COLOR,
                                opacity: 0.9,
                              },
                              '&:disabled': {
                                backgroundColor: isDark ? '#444' : '#e0e0e0',
                                color: isDark ? drawerTheme.subtleText : '#999',
                              },
                            }}
                          >
                            {savingExtendStay ? 'Saving...' : 'Save'}
                          </Button>
                        </Box>
                      )}
                    </Grid>
                  )}

                  {/* Nights / Hourly and Rooms */}
                  <Grid item xs={6}>
                    <StayDurationChip
                      booking={booking}
                      isDark={isDark}
                      accentColor={CUSTOM_BORDER_COLOR}
                      captionSx={{ color: isDark ? drawerTheme.subtleText : '#666' }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                        Number of Rooms:
                      </Typography>
                      <Chip
                        icon={<HotelIcon sx={{ fontSize: 12, color: CUSTOM_BORDER_COLOR }} />}
                        label={booking.total_rooms || booking.rooms || 0}
                        size="small"
                        sx={{
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                          color: CUSTOM_BORDER_COLOR,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 22,
                          border: `1px solid ${CUSTOM_BORDER_COLOR}40`,
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Allocated Rooms by Type */}
              {allocatedRooms && allocatedRooms.length > 0 ? (
                <Box>
                  <Typography variant="subtitle1" sx={{ color: isDark ? drawerTheme.text : '#333', mb: 1.5, fontWeight: 600 }}>
                    {booking.status === 'checked_out' ? 'Allocated Rooms (Checked Out)' : 'Select Rooms to Check-Out'}
                  </Typography>
                  {allocatedRooms.map((roomTypeGroup) => {
                    const roomTypeImage = roomTypeGroup.room_type?.image || roomTypeGroup.room_type?.image_url || null;
                    return (
                      <Box key={roomTypeGroup.room_type.id} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              color: isDark ? drawerTheme.text : '#333',
                              fontWeight: 500,
                            }}
                          >
                            {roomTypeGroup.room_type.name}
                          </Typography>
                          <Chip
                            label={`${roomTypeGroup.rooms.length} room${roomTypeGroup.rooms.length > 1 ? 's' : ''}`}
                            size="small"
                            sx={{
                              backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
                              color: isDark ? drawerTheme.text : '#333',
                              fontSize: '0.7rem',
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {[...(roomTypeGroup.rooms || [])]
                            // Show checked-out rooms last
                            .sort((a, b) => {
                              const aCheckedOut = a.checkout === true ? 1 : 0;
                              const bCheckedOut = b.checkout === true ? 1 : 0;
                              return aCheckedOut - bCheckedOut;
                            })
                            .map((room) => {
                            // Find guests for this room
                            const itemsForRoom = bookingItems.filter((item) => {
                              const itemRoomId = item.allocated_room_id;
                              return itemRoomId === room.id;
                            });
                            
                            // Check if all items for this room are checked out
                            const allItemsCheckedOut = itemsForRoom.length > 0 && itemsForRoom.every(item => item.checkout === true);
                            const isSelected = selectedRooms.includes(room.id);
                            const isBookingCheckedOut = booking.status === 'checked_out';
                            const isRoomCheckedOut =
                              allItemsCheckedOut || (itemsForRoom.length === 0 && room.checkout === true);
                            const isDisabled =
                              isBookingCheckedOut || isRoomCheckedOut || !hasPendingCheckoutItems;
                            const roomImage = room.image || room.image_url || roomTypeImage || null;
                            const hasRoomChangedInSequence = itemsForRoom.some((item) =>
                              changedSequenceKeys.has(`${item.room_type_id}_${item.room_sequence || 1}`)
                            );
                            const hasRoomTypeUpgradedInSequence = itemsForRoom.some((item) => {
                              const rtId = Number((item.room_type_id ?? item.room_type?.id) || 0);
                              const seq = item.room_sequence || 1;
                              return rtId && upgradedRoomTypeKeys.has(`${seq}_${rtId}`);
                            });
                            const roomTypeChangeKind = hasRoomTypeUpgradedInSequence
                              ? resolveRoomTypeChangeKindFromItems(
                                itemsForRoom,
                                roomTypeSortOrderById,
                                bookingItems,
                              )
                              : 'upgrade';
                            const roomTypeChangeLabel = getRoomTypeChangeLabel(roomTypeChangeKind);
                            const roomTypeChangeChipSx = roomTypeChangeKind === 'downgrade'
                              ? {
                                backgroundColor: isDark ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.12)',
                                color: '#f59e0b',
                                border: `1px solid ${isDark ? 'rgba(245, 158, 11, 0.45)' : 'rgba(245, 158, 11, 0.35)'}`,
                              }
                              : {
                                backgroundColor: isDark ? 'rgba(184, 134, 11, 0.2)' : 'rgba(184, 134, 11, 0.12)',
                                color: '#b8860b',
                                border: `1px solid ${isDark ? 'rgba(184, 134, 11, 0.45)' : 'rgba(184, 134, 11, 0.35)'}`,
                              };
                            
                            // Get all unique guests for this room
                            const roomGuests = [];
                            const guestMap = new Map();
                            itemsForRoom.forEach((item) => {
                              const itemGuests = item.guests && item.guests.length > 0 ? item.guests : [];
                              itemGuests.forEach(guest => {
                                const guestKey = guest.id || `${guest.first_name}-${guest.last_name}-${guest.email}`;
                                if (!guestMap.has(guestKey)) {
                                  guestMap.set(guestKey, true);
                                  roomGuests.push(guest);
                                }
                              });
                            });

                            return (
                                <Card
                                key={room.id}
                                elevation={3}
                                  sx={{
                                  backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                                  borderRadius: 3,
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                  cursor: isDisabled ? 'default' : 'pointer',
                                  opacity: isDisabled ? 0.7 : 1,
                                  border: isSelected
                                    ? `2px solid ${CUSTOM_BORDER_COLOR}`
                                    : `1px solid ${isDark ? 'rgba(158, 158, 158, 0.3)' : 'rgba(158, 158, 158, 0.2)'}`,
                                  '&:hover': !isDisabled ? {
                                          transform: 'translateY(-2px)',
                                    boxShadow: isDark
                                      ? '0 8px 16px rgba(76, 225, 182, 0.3)'
                                      : '0 8px 16px rgba(76, 225, 182, 0.2)',
                                  } : {},
                                }}
                              >
                                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', minHeight: 100, minWidth: { xs: 0 } }}>
                                    {/* Room Type Image - Left Side */}
                                    <Box
                                          sx={{
                                        ...bookingDrawerRoomImageSizeSx,
                                        position: 'relative',
                                        backgroundImage: roomImage
                                          ? `linear-gradient(135deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.35) 100%), url(${roomImage})`
                                          : `linear-gradient(135deg, ${isDark ? '#3a3a4a' : '#e8e8e8'} 0%, ${isDark ? '#2a2a3a' : '#d8d8d8'} 100%)`,
                                        backgroundColor: isDark ? '#3a3a4a' : '#e8e8e8',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        borderTopLeftRadius: 12,
                                        borderBottomLeftRadius: 12,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        overflow: 'hidden',
                                        }}
                                      >
                                      {!roomImage && (
                                        <RoomIcon sx={{ fontSize: 40, color: isDark ? drawerTheme.subtleText : '#999', opacity: 0.5 }} />
                                    )}
                                      
                                    {/* Room Number Badge on Image */}
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        bottom: 8,
                                        left: 8,
                                          right: 8,
                                          zIndex: 1,
                                        }}
                                      >
                                        <Chip
                                          icon={<RoomIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                                          label={room.room_number}
                                          size="small"
                                      sx={{
                                            backgroundColor: 'rgba(0,0,0,0.75)',
                                            color: '#fff',
                                            fontWeight: 700,
                                            fontSize: '0.75rem',
                                            height: 24,
                                            backdropFilter: 'blur(10px)',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                            '& .MuiChip-icon': {
                                              color: '#fff !important',
                                              fontSize: 14,
                                            },
                                          }}
                                        />
                                    </Box>
                                    </Box>
                                    
                                    {/* Guest Information - Right Side */}
                                    <Box sx={{ 
                                      flex: 1,
                                      minWidth: { xs: 0 },
                                      p: 1.5, 
                                        display: 'flex',
                                      flexDirection: 'column', 
                                      justifyContent: 'space-between',
                                      background: isDark 
                                        ? drawerTheme.surfaceGradient
                                        : 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
                                    }}>
                                      {/* Room Type with Rate Plan and Checkbox */}
                                      {(() => {
                                        // Get booking items for this room to get rate plan
                                        const firstItem = itemsForRoom.length > 0 ? itemsForRoom[0] : null;
                                        const ratePlanName = firstItem?.rate_plan?.name || null;
                                        
                                        return (
                                          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75, flexWrap: 'wrap', width: '100%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: { xs: 0 }, flex: { xs: '1 1 150px', sm: 1 } }}>
                                              <HotelIcon sx={{ fontSize: 16, color: CUSTOM_BORDER_COLOR, flexShrink: 0 }} />
                                              <Typography
                                                variant="subtitle2"
                                                sx={{
                                                  color: isDark ? '#fff' : '#1a1a1a',
                                                  fontWeight: 700,
                                                  fontSize: '0.85rem',
                                                  letterSpacing: '0.01em',
                                                  lineHeight: 1.2,
                                                  minWidth: { xs: 0 },
                                                  maxWidth: { xs: '100%', sm: 'none' },
                                                }}
                                              >
                                                {roomTypeGroup.room_type.name}
                                              </Typography>
                                              {ratePlanName && (
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    color: isDark ? drawerTheme.subtleText : '#666',
                                                    fontSize: '0.7rem',
                                                    fontStyle: 'italic',
                                                  }}
                                                >
                                                  ({ratePlanName})
                                                </Typography>
                                              )}
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, flexWrap: 'wrap' }}>
                                              {/* Checkout Status Chip */}
                                              {isRoomCheckedOut && (
                                                <Chip
                                                  label="Checked Out"
                                                  size="small"
                                                  sx={{
                                                    backgroundColor: isDark ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)',
                                                    color: '#4caf50',
                                                    fontWeight: 600,
                                                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                                    height: { xs: 20, sm: 22 },
                                                    border: `1px solid ${isDark ? 'rgba(76, 175, 80, 0.4)' : 'rgba(76, 175, 80, 0.3)'}`,
                                                  }}
                                                />
                                              )}
                                              {hasRoomChangedInSequence && (
                                                <Chip
                                                  label="Room Change"
                                                  size="small"
                                                  sx={{
                                                    backgroundColor: isDark ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.12)',
                                                    color: '#ff9800',
                                                    fontWeight: 600,
                                                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                                    height: { xs: 20, sm: 22 },
                                                    border: `1px solid ${isDark ? 'rgba(255, 152, 0, 0.45)' : 'rgba(255, 152, 0, 0.35)'}`,
                                                  }}
                                                />
                                              )}
                                              {hasRoomTypeUpgradedInSequence && (
                                                <Chip
                                                  label={roomTypeChangeLabel}
                                                  size="small"
                                                  sx={{
                                                    ...roomTypeChangeChipSx,
                                                    fontWeight: 600,
                                                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                                    height: { xs: 20, sm: 22 },
                                                  }}
                                                />
                                              )}
                                              {/* Checkbox for Selection */}
                                              {!isDisabled && (
                                        <Checkbox
                                          checked={isSelected}
                                                  onChange={() => handleRoomToggle(room.id)}
                                          size="small"
                                          sx={{
                                                    color: CUSTOM_BORDER_COLOR,
                                            '&.Mui-checked': {
                                                      color: CUSTOM_BORDER_COLOR,
                                            },
                                          }}
                                        />
                                              )}
                                            </Box>
                                    </Box>
                                        );
                                      })()}
                                      
                                      {/* Guest Slider Section */}
                                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        {roomGuests.length > 0 ? (
                                          <Box sx={{ position: 'relative', minHeight: 50, py: 0.5 }}>
                                            {(() => {
                                              const currentSlideIndex = guestSlideIndex[room.id] || 0;
                                              const safeIndex = Math.min(Math.max(0, currentSlideIndex), roomGuests.length - 1);
                                              const currentGuest = roomGuests[safeIndex];
                                              
                                              if (!currentGuest) {
                                                return (
                                                  <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666', textAlign: 'center' }}>
                                                    No guest data available
                                                  </Typography>
                                                );
                                              }
                                              
                                              const guestName = currentGuest.name || `${currentGuest.first_name || ''} ${currentGuest.middle_name || ''} ${currentGuest.last_name || ''}`.trim() || 'Guest';
                                              const guestPhone = currentGuest.phone || '—';
                                              
                                              return (
                                    <Box
                                      sx={{
                                                    position: 'relative',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                                    gap: 1,
                                                  }}
                                                >
                                                  {/* Previous Button */}
                                                  {roomGuests.length > 1 && (
                                                    <IconButton
                                        size="small"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        const prevIndex = safeIndex > 0 ? safeIndex - 1 : roomGuests.length - 1;
                                                        setGuestSlideIndex(prev => ({ ...prev, [room.id]: prevIndex }));
                                                      }}
                                        sx={{
                                                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                                                        color: CUSTOM_BORDER_COLOR,
                                                        border: `1px solid ${CUSTOM_BORDER_COLOR}40`,
                                                        width: 32,
                                                        height: 32,
                                                        '&:hover': {
                                                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.25)' : 'rgba(76, 225, 182, 0.2)',
                                                          transform: 'scale(1.1)',
                                                        },
                                                      }}
                                                    >
                                                      <ChevronLeftIcon sx={{ fontSize: 20 }} />
                                                    </IconButton>
                                                  )}
                                                  
                                                  {/* Guest Info Card */}
                                                  <Paper
                                                    elevation={0}
                                                    sx={{
                                                      flex: 1,
                                                      minWidth: { xs: 0 },
                                                      p: 1,
                                                      backgroundColor: isDark ? 'rgba(76, 225, 182, 0.08)' : 'rgba(76, 225, 182, 0.05)',
                                                      borderRadius: 1.5,
                                                      border: `1px solid ${CUSTOM_BORDER_COLOR}30`,
                                                    }}
                                                  >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                      {/* Avatar */}
                                                      <Avatar
                                                        sx={{
                                                          width: 32,
                                                          height: 32,
                                                          bgcolor: CUSTOM_BORDER_COLOR,
                                                          color: '#000',
                                                          fontWeight: 700,
                                                          fontSize: '0.85rem',
                                                          flexShrink: 0,
                                                        }}
                                                      >
                                                        {guestName.charAt(0).toUpperCase()}
                                                      </Avatar>
                                                      
                                                      {/* Name and Phone */}
                                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <DrawerGuestFieldLink
                                                          isDark={isDark}
                                                          guest={currentGuest}
                                                          guestId={currentGuest?.id}
                                                          booking={booking}
                                                          variant="body2"
                                                          onNavigate={onClose}
                                                          sx={{
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            mb: 0.25,
                                                            textOverflow: 'ellipsis',
                                                            overflow: 'hidden',
                                                            whiteSpace: 'nowrap',
                                                            display: 'block',
                                                          }}
                                                        >
                                                          {guestName}
                                                        </DrawerGuestFieldLink>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                          <PhoneIcon sx={{ fontSize: 12, color: CUSTOM_BORDER_COLOR }} />
                                      <DrawerGuestFieldLink
                                        isDark={isDark}
                                        guest={currentGuest}
                                        guestId={currentGuest?.id}
                                        booking={booking}
                                        variant="caption"
                                        onNavigate={onClose}
                                        sx={{
                                                              fontSize: '0.7rem',
                                                              fontWeight: 500,
                                                            }}
                                      >
                                                            {guestPhone}
                                      </DrawerGuestFieldLink>
                                                        </Box>
                                                        {roomGuests.length > 1 && (
                                                          <Chip
                                                            label={`${safeIndex + 1} / ${roomGuests.length}`}
                                                            size="small"
                                                            sx={{
                                                              mt: 0.5,
                                                              height: 18,
                                                              fontSize: '0.6rem',
                                                              bgcolor: isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)',
                                                              color: CUSTOM_BORDER_COLOR,
                                                              fontWeight: 600,
                                                            }}
                                                          />
                                                        )}
                                    </Box>
                                                      
                                                      {/* View Document Icon */}
                                                      <IconButton
                                                        size="small"
                                                        onClick={async (e) => {
                                                          e.stopPropagation();
                                                          try {
                                                            const docData = await getBookingGuestDocuments(bookingId, currentGuest.id);
                                                            setSelectedGuestDocuments(buildGuestDocumentsPayload({
                                                              docData,
                                                              guest: currentGuest,
                                                              booking: { ...booking, payments: paymentHistory },
                                                              guestName,
                                                              guestPhone: currentGuest.phone || booking?.guest_phone || booking?.phone,
                                                              roomNumber: room.room_number,
                                                            }));
                                                            setDocumentDrawerOpen(true);
                                                          } catch (err) {
                                                            setSelectedGuestDocuments(buildGuestDocumentsPayload({
                                                              guest: currentGuest,
                                                              booking: { ...booking, payments: paymentHistory },
                                                              guestName,
                                                              guestPhone: currentGuest.phone || booking?.guest_phone || booking?.phone,
                                                              roomNumber: room.room_number,
                                                            }));
                                                            setDocumentDrawerOpen(true);
                                                          }
                                                        }}
                                                        sx={{
                                                          color: (currentGuest.has_id_front || currentGuest.has_id_back) 
                                                            ? CUSTOM_BORDER_COLOR 
                                                            : (isDark ? drawerTheme.subtleText : '#999'),
                                                          opacity: (currentGuest.has_id_front || currentGuest.has_id_back) ? 1 : 0.5,
                                                          '&:hover': {
                                                            backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                                                            opacity: 1,
                                                          },
                                                          width: 28,
                                                          height: 28,
                                                        }}
                                                      >
                                                        <VisibilityIcon sx={{ fontSize: 16 }} />
                                                      </IconButton>
                                  </Box>
                                                  </Paper>
                                                  
                                                  {/* Next Button */}
                                                  {roomGuests.length > 1 && (
                                                    <IconButton
                                                      size="small"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        const nextIndex = safeIndex < roomGuests.length - 1 ? safeIndex + 1 : 0;
                                                        setGuestSlideIndex(prev => ({ ...prev, [room.id]: nextIndex }));
                                                      }}
                                        sx={{
                                                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                                                        color: CUSTOM_BORDER_COLOR,
                                                        border: `1px solid ${CUSTOM_BORDER_COLOR}40`,
                                                        width: 32,
                                                        height: 32,
                                                        '&:hover': {
                                                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.25)' : 'rgba(76, 225, 182, 0.2)',
                                                          transform: 'scale(1.1)',
                                                        },
                                                      }}
                                                    >
                                                      <ChevronRightIcon sx={{ fontSize: 20 }} />
                                                    </IconButton>
                                                  )}
                                                </Box>
                                              );
                                            })()}
                                          </Box>
                                        ) : (
                                          <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666', textAlign: 'center' }}>
                                            No guests assigned
                                      </Typography>
                                    )}
                                      </Box>
                                    </Box>
                                  </Box>
                                  </CardContent>
                                </Card>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                !loading && (
                  <Alert severity="info">
                    No allocated rooms found for this booking.
                  </Alert>
                )
              )}

              {/* Payment Summary - Show for all bookings (including checked out for add payment) */}
              {!loading && booking && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 1.5, borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0' }} />
                  
                  <BookingDrawerPaymentCard
                    booking={booking}
                    payments={paymentHistory}
                    isDark={isDark}
                    drawerTheme={drawerTheme}
                    accentColor={CUSTOM_BORDER_COLOR}
                    onAddPayment={() => {
                      setPaymentDrawerOpen(true);
                    }}
                    onRestaurantPaymentsChanged={async () => {
                      onBookingUpdated?.(bookingId);
                      void fetchAllocatedRooms(false);
                    }}
                    clearMessage="All payments have been collected. You can proceed with checkout."
                  />
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 1.5,
            borderTop: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
          }}
        >
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
          <Button
            variant="outlined"
            fullWidth
            onClick={onClose}
            disabled={submitting}
            sx={{
              borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
              color: isDark ? drawerTheme.text : '#666',
              '&:hover': {
                borderColor: CUSTOM_BORDER_COLOR,
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                color: CUSTOM_BORDER_COLOR,
              },
              '&:disabled': {
                borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                color: isDark ? drawerTheme.subtleText : '#999',
              },
            }}
          >
            Cancel
          </Button>
            </Grid>
            <Grid item xs={6}>
          {showModifyBooking || showDisabledModifyBooking ? (
            (() => {
              const modifyButton = (
            <Button
              variant="contained"
              fullWidth
              disabled={showDisabledModifyBooking}
              onClick={() => {
                if (!canEditBooking || !onModifyBooking) return;
                const safeBookingId = Number(bookingId);
                if (!Number.isInteger(safeBookingId) || safeBookingId <= 0) {
                  setError('Invalid booking reference. Please refresh and try again.');
                  return;
                }
                onModifyBooking(safeBookingId);
              }}
              sx={{
                backgroundColor: CUSTOM_BORDER_COLOR,
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: CUSTOM_BORDER_COLOR,
                  opacity: 0.9,
                  boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                },
                '&:disabled': {
                  backgroundColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                  color: isDark ? drawerTheme.subtleText : '#666',
                },
              }}
            >
              Modify booking
            </Button>
              );
              return showDisabledModifyBooking && !canEditBooking ? (
                <Tooltip title={getPermissionDeniedMessage(PERMISSIONS.BOOKING_MODIFY, 'Modify Booking')} arrow>
                  <span style={{ width: '100%', display: 'block' }}>{modifyButton}</span>
                </Tooltip>
              ) : modifyButton;
            })()
          ) : (
            <Button
              variant="contained"
              fullWidth
              onClick={handleCheckOut}
              disabled={submitting || !canCheckOut || isBookingCheckedOut}
              sx={{
                backgroundColor: CUSTOM_BORDER_COLOR,
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: CUSTOM_BORDER_COLOR,
                  opacity: 0.9,
                  boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                },
                '&:disabled': {
                  backgroundColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                  color: isDark ? drawerTheme.subtleText : '#666',
                },
              }}
            >
              {submitting ? (
                <CircularProgress size={20} />
              ) : isPartialCheckOut ? (
                'Partial Check-Out'
              ) : (
                'Check-Out'
              )}
            </Button>
          )}
            </Grid>
          </Grid>
        </Box>

        {/* Due Confirmation Popup - Inside drawer */}
        {dueConfirmOpen && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)',
              zIndex: 1390,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
            onClick={() => {
              if (submitting) return;
              setDueConfirmOpen(false);
              setDueConfirmInput('');
              setDueConfirmError('');
            }}
          >
            <Paper
              onClick={(e) => e.stopPropagation()}
              sx={{
                backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                borderRadius: 2,
                boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.2)',
                minWidth: 400,
                maxWidth: 520,
                p: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: isDark ? drawerTheme.text : '#333',
                  fontWeight: 700,
                  mb: 1,
                }}
              >
                Outstanding Due Amount
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? drawerTheme.subtleText : '#555',
                  mb: 0.5,
                }}
              >
                Remaining amount is <strong>{formatCurrency(dueConfirmAmount, booking?.currency || 'INR')}</strong>
                {(() => {
                  const breakdown = getOutstandingDueBreakdown();
                  if (!breakdown) return null;
                  const parts = [];
                  if (breakdown.hotelDue > 0.009) {
                    parts.push(`Hotel ${formatCurrency(breakdown.hotelDue, breakdown.currency)}`);
                  }
                  if (breakdown.restaurantDue > 0.009) {
                    parts.push(`Restaurant ${formatCurrency(breakdown.restaurantDue, breakdown.currency)}`);
                  }
                  if (parts.length === 0) return null;
                  return <> ({parts.join(' · ')})</>;
                })()}
                .
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? drawerTheme.subtleText : '#666',
                  mb: 2,
                }}
              >
                Type <strong>confirm</strong> to proceed with checkout with pending payment.
              </Typography>
              <TextField
                fullWidth
                autoFocus
                label='Type "confirm"'
                value={dueConfirmInput}
                onChange={(e) => {
                  setDueConfirmInput(e.target.value);
                  if (dueConfirmError) {
                    setDueConfirmError('');
                  }
                }}
                error={Boolean(dueConfirmError)}
                helperText={dueConfirmError || ' '}
                sx={{
                  mb: 1.5,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
                  },
                }}
              />
              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setDueConfirmOpen(false);
                    setDueConfirmInput('');
                    setDueConfirmError('');
                  }}
                  disabled={submitting}
                  sx={{
                    borderColor: isDark ? '#555' : '#ccc',
                    color: isDark ? drawerTheme.text : '#666',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmDueCheckout}
                  disabled={submitting}
                  sx={{
                    backgroundColor: CUSTOM_BORDER_COLOR,
                    color: '#000',
                    fontWeight: 700,
                    '&:hover': {
                      backgroundColor: CUSTOM_BORDER_COLOR,
                      opacity: 0.9,
                    },
                  }}
                >
                  {submitting ? <CircularProgress size={20} /> : 'Confirm Checkout'}
                </Button>
              </Box>
            </Paper>
          </Box>
        )}

        {/* Error Popup - Inside drawer */}
        {error && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)',
              zIndex: 1400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
            onClick={() => setError(null)}
          >
            <Paper
              onClick={(e) => e.stopPropagation()}
              sx={{
                backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                borderRadius: 2,
                boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.2)',
                minWidth: 400,
                maxWidth: 500,
                p: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: isDark ? drawerTheme.text : '#333',
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                Error
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: isDark ? drawerTheme.subtleText : '#666',
                  mb: 3,
                }}
              >
                {error}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  onClick={() => setError(null)}
                  variant="contained"
                  sx={{
                    backgroundColor: CUSTOM_BORDER_COLOR,
                    color: '#000',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: CUSTOM_BORDER_COLOR,
                      opacity: 0.9,
                    },
                  }}
                >
                  OK
                </Button>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* Payment Drawer */}
      <Drawer
        anchor="right"
        open={paymentDrawerOpen}
        onClose={() => {
          setPaymentDrawerOpen(false);
          setPaymentAmount(0);
          setPaymentDate(moment());
          setPaymentMethod('');
          setPaymentLinkSendOpen(false);
          setReferenceNumber('');
          setPaymentReceipt(null);
          setPaymentCameraUpload(false);
        }}
        sx={getDrawerRootSx()}
        SlideProps={{ direction: 'left' }}
        transitionDuration={{ enter: 350, exit: 250 }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
          },
        }}
      >
        <Box ref={paymentDrawerRootRef} sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
          {/* Header */}
          <Box
            sx={{
              borderBottom: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
              backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1.5,
              }}
            >
              <PaymentDrawerHeaderTitle
                booking={booking}
                bookingId={bookingId}
                paymentHistory={paymentHistory}
                isDark={isDark}
                drawerTheme={drawerTheme}
                accentColor={CUSTOM_BORDER_COLOR}
              />
              <IconButton onClick={() => {
                setPaymentDrawerOpen(false);
                setPaymentAmount(0);
                setPaymentDate(moment());
                setPaymentMethod('');
                setPaymentLinkSendOpen(false);
                setReferenceNumber('');
                setPaymentReceipt(null);
                setPaymentCameraUpload(false);
              }} sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                <CloseIcon />
              </IconButton>
            </Box>
            {/* Progress Bar */}
            {submittingPayment && (
              <LinearProgress
                sx={{
                  height: 3,
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: CUSTOM_BORDER_COLOR,
                  },
                }}
              />
            )}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {/* Payment Summary */}
            {booking && (
              <BookingPaymentSummaryCard
                summary={getPaymentSummaryData(booking, paymentHistory)}
                booking={booking}
                bookingId={bookingId}
                isDark={isDark}
                drawerTheme={drawerTheme}
                overlayMountRef={paymentDrawerRootRef}
                onExtraChargesSaved={async (result) => {
                  if (result?.booking) {
                    setBooking(result.booking);
                  } else {
                    await fetchAllocatedRooms(false);
                  }
                }}
              />
            )}

            {/* Payment History */}
            {(loadingPayments) ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, mb: 2 }}>
                <CircularProgress size={24} sx={{ color: CUSTOM_BORDER_COLOR }} />
              </Box>
            ) : (
              <BookingFinancialHistoryPanel
                payments={Array.isArray(paymentHistory) ? paymentHistory : []}
                currency={booking?.currency || 'INR'}
                hotelTimezone={booking?.hotel_timezone}
                isDark={isDark}
                drawerTheme={drawerTheme}
                accent={CUSTOM_BORDER_COLOR}
                defaultExpanded={false}
              />
            )}

            {/* Collect Payment Form */}
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                borderRadius: '12px',
              }}
            >
              <Typography variant="subtitle2" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 600, mb: 1.5 }}>
                Collect Payment
              </Typography>

              <Grid container spacing={1.5}>
                {/* Payment Amount */}
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Payment Amount"
                    type="number"
                    value={paymentAmount != null && paymentAmount !== '' ? round2(Number(paymentAmount)) : ''}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      setPaymentAmount(Number.isFinite(value) && value >= 0 ? round2(value) : 0);
                    }}
                    inputProps={{
                      step: "0.01",
                      min: "0",
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {bookingCurrencySymbol}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
                        '& fieldset': {
                          borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateTimePicker
                      label="Payment Date & Time"
                      disableFuture
                      value={paymentDate}
                      onChange={setPaymentDate}
                      slotProps={{
                        ...datePickerSlotProps,
                        textField: {
                          fullWidth: true,
                          size: 'medium',
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
                              '& fieldset': {
                                borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                              },
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                {/* Payment Method - Chips */}
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', fontWeight: 600, mb: 1, display: 'block' }}>
                    Payment Method *
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {paymentMethods.map((method) => (
                      <Chip
                        key={method}
                        label={method}
                        onClick={() => {
                          setPaymentMethod(method);
                          setPaymentLinkSendOpen(method === PAYMENT_LINK_METHOD && canSendPaymentLink);
                        }}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: paymentMethod === method
                            ? CUSTOM_BORDER_COLOR
                            : (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)'),
                          color: paymentMethod === method
                            ? '#000'
                            : CUSTOM_BORDER_COLOR,
                          border: `1px solid ${paymentMethod === method
                            ? CUSTOM_BORDER_COLOR
                            : (isDark ? 'rgba(76, 225, 182, 0.3)' : 'rgba(76, 225, 182, 0.25)')}`,
                          fontWeight: paymentMethod === method ? 700 : 600,
                          fontSize: '0.75rem',
                          height: 32,
                          '&:hover': {
                            backgroundColor: paymentMethod === method
                              ? CUSTOM_BORDER_COLOR
                              : (isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'),
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Grid>

                {/* Reference Number */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Reference Number (Optional)"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
                        '& fieldset': {
                          borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Payment Receipt Upload */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: isDark ? drawerTheme.text : '#333' }}>
                        Payment Receipt (Optional)
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}>
                        <FormControlLabel
                          label="Camera upload"
                          labelPlacement="start"
                          control={
                            <Switch
                              checked={paymentCameraUpload}
                              onChange={(e) => setPaymentCameraUpload(e.target.checked)}
                              color="primary"
                            />
                          }
                          sx={{ color: isDark ? drawerTheme.text : '#333' }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Paper
                    sx={{
                      p: 2,
                      border: `2px dashed ${CUSTOM_BORDER_COLOR}`,
                      borderRadius: 2,
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                      minHeight: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                    onClick={handlePaymentReceiptClick}
                  >
                    {paymentReceipt ? (
                      <Box>
                        <img
                          src={paymentReceipt}
                          alt="Payment Receipt"
                          style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: 4 }}
                        />
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPaymentReceipt(null);
                          }}
                          sx={{ mt: 1 }}
                        >
                          Remove
                        </Button>
                      </Box>
                    ) : (
                      <Box>
                        {paymentCameraUpload ? (
                          <CameraAltIcon sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }} />
                        ) : (
                          <CloudUploadIcon sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }} />
                        )}
                        <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                          {paymentCameraUpload ? 'Click to capture from camera' : 'Click to upload or drag and drop'}
                        </Typography>
                      </Box>
                    )}
                    <input
                      ref={paymentReceiptFileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handlePaymentReceiptUpload}
                    />
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 1.5,
              borderTop: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
              backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
            }}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setPaymentDrawerOpen(false);
                    setPaymentAmount(0);
                    setPaymentDate(moment());
                    setPaymentMethod('');
                    setPaymentLinkSendOpen(false);
                    setReferenceNumber('');
                    setPaymentReceipt(null);
                    setPaymentCameraUpload(false);
                  }}
                  disabled={submittingPayment}
                  sx={{
                    borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                    color: isDark ? drawerTheme.text : '#666',
                    '&:hover': {
                      borderColor: CUSTOM_BORDER_COLOR,
                      backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                      color: CUSTOM_BORDER_COLOR,
                    },
                    '&:disabled': {
                      borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                      color: isDark ? drawerTheme.subtleText : '#999',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmitPayment}
                  disabled={submittingPayment || !paymentAmount || !paymentMethod || paymentMethod === 'PAYMENT LINK'}
                  sx={{
                    backgroundColor: CUSTOM_BORDER_COLOR,
                    color: '#000',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: CUSTOM_BORDER_COLOR,
                      opacity: 0.9,
                      boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                    },
                    '&:disabled': {
                      backgroundColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                      color: isDark ? drawerTheme.subtleText : '#666',
                    },
                  }}
                >
                  {submittingPayment ? <CircularProgress size={20} /> : 'Collect Payment'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {canSendPaymentLink ? (
          <PaymentLinkDrawerOverlay
            open={paymentLinkSendOpen}
            onClose={() => setPaymentLinkSendOpen(false)}
            bookingId={bookingId}
            booking={booking}
            amount={paymentAmount}
            currency={booking?.currency || 'INR'}
            isDark={isDark}
            accent={CUSTOM_BORDER_COLOR}
            canSendEmail={canSendPaymentLinkEmail}
            canSendWhatsApp={canSendPaymentLinkWhatsApp}
            onSentSuccess={(msg) => setSuccessMessage(msg)}
            onAfterSend={async () => {
              await fetchPaymentHistory(booking?.code || booking?.booking_code);
              await fetchAllocatedRooms(false);
            }}
          />
          ) : null}
        </Box>

        {/* Payment Receipt Camera Modal */}
        {paymentCameraOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              zIndex: 1500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                stopPaymentCamera();
              }
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '800px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                Capture Payment Receipt
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '600px',
                  borderRadius: 2,
                  overflow: 'hidden',
                  mb: 2,
                }}
              >
                <video
                  ref={paymentReceiptVideoRef}
                  autoPlay
                  playsInline
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                />
                <canvas ref={paymentReceiptCanvasRef} style={{ display: 'none' }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={capturePaymentReceipt}
                  sx={{
                    backgroundColor: CUSTOM_BORDER_COLOR,
                    color: '#000',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: CUSTOM_BORDER_COLOR,
                      opacity: 0.9,
                    },
                  }}
                >
                  Capture
                </Button>
                <Button
                  variant="outlined"
                  onClick={stopPaymentCamera}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Success Message Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{
            backgroundColor: CUSTOM_BORDER_COLOR,
            color: '#000',
            fontWeight: 600,
            '& .MuiAlert-icon': {
              color: '#000',
            },
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={cameraPermissionHint.open}
        autoHideDuration={9000}
        onClose={() => setCameraPermissionHint((h) => ({ ...h, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setCameraPermissionHint((h) => ({ ...h, open: false }))}
          severity="warning"
          variant="filled"
          sx={{ width: '100%', alignItems: 'center' }}
        >
          {cameraPermissionHint.message}
        </Alert>
      </Snackbar>

      <GuestDocumentsDrawer
        open={documentDrawerOpen}
        onClose={() => setDocumentDrawerOpen(false)}
        documents={selectedGuestDocuments}
        isDark={isDark}
      />

      {canViewBookingAudit && (
        <BookingAuditDrawer
          open={auditDrawerOpen}
          onClose={() => setAuditDrawerOpen(false)}
          bookingId={auditBookingId}
          bookingCode={booking?.code || booking?.booking_code}
          isDark={isDark}
        />
      )}
    </Drawer>
  );
};

export default CheckOutDrawer;

